import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import {
  Table,
  Popconfirm,
  Form,
  Typography,
  Card,
  Button,
  Divider,
  Space,
  Avatar,
  Tag,
  Collapse,
  Tooltip,
  Statistic,
} from 'antd';
import { Row, Col, Table as TableRB } from 'react-bootstrap';
import {
  PlusOutlined,
  ReloadOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  RedoOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import socketHandler from '../../config/socket';
import {
  getLeaveRequests,
  changeLeaveStatus,
  getLeaveTotals,
} from '../../redux/leaveManagement/action';
import { styles } from './style';
import moment from 'moment';
import DepartmentFilter from '../../components/departmentFilter';
import UserDetailsModal from '../../components/userDetailsModal';
import TotalLeaves from './totalLeaves';

const { Text } = Typography;
const { Panel } = Collapse;

const EditableTable = () => {
  const dispatch = useDispatch();
  const { leaves, leaveTotals, totalLeaves, initial } = useSelector(
    (state) => state.leaveManagement
  );
  const { company } = useSelector((state) => state.auth);
  const { allDeptByIds } = useSelector((state) => state.membersReducer);

  const renderData = () => {
    const data = leaves.map((item) => {
      return {
        key: item.id,
        user: item?.user,
        email: item?.user?.email,
        type: item?.type,
        from: moment(item?.start).format('DD-MM-YYYY'),
        to: moment(item?.end).format('DD-MM-YYYY'),
        operation: item?.status,
        description: item?.description,
        paid: item?.paid ? 'YES' : 'NO',
      };
    });
    return data;
  };
  const [form] = Form.useForm();
  const [data, setData] = useState(leaves.length > 0 ? renderData() : []);
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedDpt, setDpt] = useState(null);
  const [userDetailModal, setDetailModal] = useState(false);
  const [selectedUser, selectUser] = useState(null);

  const pRef = useRef(1);
  const depRef = useRef(null);

  const stopLoader = () => {
    setLoader(false);
  };

  const stopUpdateLoader = () => {
    setLoader(false);
    dispatch(getLeaveTotals(selectedDpt));
  };

  useEffect(() => {
    if (initial) {
      setLoader(true);
      dispatch(getLeaveRequests(page, selectedDpt, stopLoader));
      dispatch(getLeaveTotals(selectedDpt));
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(leaves)) {
      const data = renderData();
      setData(data);
    }
  }, [leaves]);

  const refreshData = () => {
    setLoader(true);
    dispatch(getLeaveRequests(1, selectedDpt, stopLoader));
    dispatch(getLeaveTotals(selectedDpt));
  };

  const changePage = (page) => {
    setPage(page);
    pRef.current = Number(page);
    setLoader(true);
    dispatch(getLeaveRequests(page, selectedDpt, stopLoader));
  };
  const onChangeDept = (dept) => {
    setDpt(dept);
    depRef.current = dept;
    setLoader(true);
    dispatch(getLeaveRequests(page, dept, stopLoader));
    dispatch(getLeaveTotals(dept));
  };

  const updateLeave = (status, item) => {
    setLoader(true);
    const formData = {};
    formData.status = status;
    dispatch(changeLeaveStatus(item.key, formData, item, stopUpdateLoader));
  };

  const onRowAvatar = (record) => {
    const deptName = allDeptByIds?.[record?.organization]?.name || '';

    const userRecord = {
      ...record,
      company: company?.name,
      organization: deptName,
    };

    selectUser(userRecord);
    setDetailModal(true);
  };

  const fetchUpdates = useCallback(() => {
    dispatch(getLeaveRequests(pRef?.current, depRef?.current, () => {}));
    dispatch(getLeaveTotals(depRef?.current));
  }, []);

  useEffect(() => {
    socketHandler.on('leaveRequest', fetchUpdates);
    return () => {
      socketHandler.off('leaveRequest', fetchUpdates);
    };
  }, []);

  const _renderUser = (user) => {
    return (
      <Space
        align="center"
        className="c-pointer"
        onClick={() => onRowAvatar(user)}
      >
        <Avatar src={user?.profilePic} size="large" />
        <Text strong className="pl-2 font-weight-bold">
          {user?.name}
        </Text>
      </Space>
    );
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'user',
      editable: true,
      align: 'left',
      render: _renderUser,
    },

    {
      title: 'Type',
      dataIndex: 'type',
      editable: true,
      align: 'center',
    },
    {
      title: 'Date From',
      dataIndex: 'from',
      editable: true,
      align: 'center',
    },
    {
      title: 'Date To',
      dataIndex: 'to',
      editable: true,
      align: 'center',
    },
    {
      title: 'Paid',
      dataIndex: 'paid',
      editable: true,
      align: 'center',
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      // align: "center",
      render: (_, record) => {
        if (record?.operation == 'pending') {
          return (
            <Space align="center" size="large">
              <Popconfirm
                title="Sure to Approved?"
                onConfirm={() => updateLeave('approved', record)}
              >
                <Button className="bg-primary text-white border-primary">
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Sure to Reject?"
                onConfirm={() => updateLeave('rejected', record)}
              >
                <Button>Reject</Button>
              </Popconfirm>
            </Space>
          );
        } else {
          return (
            <Tag
              color={record?.operation == 'approved' ? '#87d068' : '#f50'}
              style={styles.tag}
              className="text-uppercase"
            >
              {record?.operation}
            </Tag>
          );
        }
      },
    },
  ];

  return (
    <React.Fragment>
      <Row>
        <Col md={12}>
          <div className="row float-right mx-1">
            <DepartmentFilter
              pickerStyle={styles.pickerStyle}
              onSelectDpt={onChangeDept}
            />

            <Button
              type="primary"
              className="bg-primary border-primary mx-2"
              icon={<PlusOutlined />}
              style={styles.buttonUpload}
            >
              Add Task
            </Button>
            <Tooltip title="Fetch Latest Data">
              <Button onClick={refreshData} style={{ height: 40 }}>
                <ReloadOutlined />
              </Button>
            </Tooltip>
          </div>
        </Col>
      </Row>

      <Form form={form} component={false}>
        <Table
          className="table-curved"
          dataSource={data}
          columns={columns}
          expandable={{
            expandedRowRender: (record) => (
              <p style={{ margin: 0 }}>
                <Text strong>Reason: </Text>
                {record.description}
              </p>
            ),
            rowExpandable: (record) => record.name !== 'Not Expandable',
          }}
          pagination={{
            onChange: changePage,
            current: page,
            total: totalLeaves,
            position: ['bottomLeft'],
            pageSize: 10,
            hideOnSinglePage: true,
          }}
          scroll={data.length > 0 && { x: true }}
          loading={loader}
        />
      </Form>

      <UserDetailsModal
        isVisible={userDetailModal && selectedUser}
        title=""
        handleModal={(value) => setDetailModal(value)}
        selectedUser={selectedUser}
      />
    </React.Fragment>
  );
};

export default memo(EditableTable);
